import React, { useRef, useEffect, useCallback, useState } from "react";

// ── Token definitions ─────────────────────────────────────────────────────────

const TOKEN_GROUPS = [
    {
        label: "Name",
        tokens: [
            { label: "First Name",  value: "{{first_name}}" },
            { label: "Last Name",   value: "{{last_name}}" },
            { label: "Full Name",   value: "{{full_name}}" },
        ],
    },
    {
        label: "Contact",
        tokens: [
            { label: "Email",   value: "{{email}}" },
            { label: "Phone",   value: "{{phone}}" },
        ],
    },
    {
        label: "Company",
        tokens: [
            { label: "Company",    value: "{{company}}" },
            { label: "Job Title",  value: "{{job_title}}" },
            { label: "Industry",   value: "{{industry}}" },
            { label: "Website",    value: "{{website}}" },
            { label: "LinkedIn",   value: "{{linkedin}}" },
        ],
    },
    {
        label: "Location",
        tokens: [
            { label: "City",     value: "{{city}}" },
            { label: "State",    value: "{{state}}" },
            { label: "Address",  value: "{{address}}" },
            { label: "Zip Code", value: "{{zip_code}}" },
        ],
    },
    {
        label: "Date",
        tokens: [
            { label: "Today's Date",  value: "{{current_date}}" },
            { label: "Month",         value: "{{current_month}}" },
            { label: "Year",          value: "{{current_year}}" },
        ],
    },
    {
        label: "Lead",
        tokens: [
            { label: "Priority",  value: "{{priority}}" },
            { label: "Source",    value: "{{source}}" },
        ],
    },
];

// ── Icons ─────────────────────────────────────────────────────────────────────

const BoldIcon      = () => <strong style={{ fontFamily: "Georgia,serif", fontSize: 13 }}>B</strong>;
const ItalicIcon    = () => <em     style={{ fontFamily: "Georgia,serif", fontSize: 13 }}>I</em>;
const UnderIcon     = () => <span   style={{ textDecoration: "underline", fontSize: 13 }}>U</span>;
const StrikeIcon    = () => <span   style={{ textDecoration: "line-through", fontSize: 13 }}>S</span>;
const H1Icon        = () => <span style={{ fontSize: 12, fontWeight: 700 }}>H1</span>;
const H2Icon        = () => <span style={{ fontSize: 12, fontWeight: 700 }}>H2</span>;

const BulletIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <line x1="9" y1="6" x2="20" y2="6"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="18" x2="20" y2="18"/>
        <circle cx="4" cy="6" r="1.5" fill="currentColor" stroke="none"/>
        <circle cx="4" cy="12" r="1.5" fill="currentColor" stroke="none"/>
        <circle cx="4" cy="18" r="1.5" fill="currentColor" stroke="none"/>
    </svg>
);

const NumberedIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/>
        <path d="M4 6h1v4M4 10h2" strokeWidth="1.8"/><path d="M4 14h2l-2 4h2" strokeWidth="1.8"/>
    </svg>
);

const QuoteIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/>
        <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/>
    </svg>
);

const LinkIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
    </svg>
);

const AlignLeftIcon   = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/>
    </svg>
);
const AlignCenterIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/>
    </svg>
);
const AlignRightIcon  = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="6" y1="18" x2="21" y2="18"/>
    </svg>
);

const HRIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <line x1="3" y1="12" x2="21" y2="12"/>
        <line x1="3" y1="6"  x2="8"  y2="6"/>
        <line x1="3" y1="18" x2="8"  y2="18"/>
    </svg>
);

const CodeIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
    </svg>
);

const FmtBtn = ({ onMouseDown, title, children }) => (
    <button type="button" className="step-editor-fmt-btn" onMouseDown={onMouseDown} title={title}>
        {children}
    </button>
);

// ── Token bar (shared between both modes) ─────────────────────────────────────

const TokenBar = ({ onInsert }) => (
    <div className="step-editor-token-bar">
        <span className="step-editor-token-bar__label">Insert:</span>
        {TOKEN_GROUPS.map((group) => (
            <span key={group.label} className="step-editor-token-group">
                <span className="step-editor-token-group__label">{group.label}</span>
                {group.tokens.map((t) => (
                    <button
                        key={t.value}
                        type="button"
                        className="crm-token-chip"
                        onClick={() => onInsert(t.value)}
                        title={`Insert ${t.value}`}
                    >
                        {t.label}
                    </button>
                ))}
            </span>
        ))}
    </div>
);

// ── Rich text editor (visual mode) ────────────────────────────────────────────

const RichEditor = ({ value, onChange }) => {
    const editorRef  = useRef(null);
    const lastHtml   = useRef(value || "");

    useEffect(() => {
        const el = editorRef.current;
        if (!el) return;
        if (value !== lastHtml.current) {
            lastHtml.current = value || "";
            el.innerHTML = value || "";
        }
    }, [value]);

    const emit = useCallback(() => {
        const html = editorRef.current?.innerHTML || "";
        if (html !== lastHtml.current) {
            lastHtml.current = html;
            onChange(html);
        }
    }, [onChange]);

    const fmt = (e, cmd, val = null) => {
        e.preventDefault();
        editorRef.current?.focus();
        document.execCommand(cmd, false, val);
        emit();
    };

    const insertLink = (e) => {
        e.preventDefault();
        const url = window.prompt("Enter URL:", "https://");
        if (!url) return;
        editorRef.current?.focus();
        document.execCommand("createLink", false, url);
        emit();
    };

    const insertHR = (e) => {
        e.preventDefault();
        editorRef.current?.focus();
        document.execCommand("insertHTML", false, "<hr style='border:none;border-top:1px solid #e2e8f0;margin:12px 0'/>");
        emit();
    };

    const insertToken = (token) => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) {
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            sel?.removeAllRanges();
            sel?.addRange(range);
        }
        document.execCommand("insertText", false, token);
        emit();
    };

    return (
        <>
            <div className="step-editor-toolbar">
                <FmtBtn onMouseDown={(e) => fmt(e, "bold")}              title="Bold">        <BoldIcon />        </FmtBtn>
                <FmtBtn onMouseDown={(e) => fmt(e, "italic")}            title="Italic">      <ItalicIcon />      </FmtBtn>
                <FmtBtn onMouseDown={(e) => fmt(e, "underline")}         title="Underline">   <UnderIcon />       </FmtBtn>
                <FmtBtn onMouseDown={(e) => fmt(e, "strikeThrough")}     title="Strikethrough"><StrikeIcon />     </FmtBtn>
                <span className="step-editor-sep" />
                <FmtBtn onMouseDown={(e) => fmt(e, "formatBlock", "h1")} title="Heading 1">   <H1Icon />          </FmtBtn>
                <FmtBtn onMouseDown={(e) => fmt(e, "formatBlock", "h2")} title="Heading 2">   <H2Icon />          </FmtBtn>
                <span className="step-editor-sep" />
                <FmtBtn onMouseDown={(e) => fmt(e, "insertUnorderedList")}       title="Bullet list">   <BulletIcon />   </FmtBtn>
                <FmtBtn onMouseDown={(e) => fmt(e, "insertOrderedList")}         title="Numbered list"> <NumberedIcon /> </FmtBtn>
                <FmtBtn onMouseDown={(e) => fmt(e, "formatBlock", "blockquote")} title="Blockquote">    <QuoteIcon />    </FmtBtn>
                <span className="step-editor-sep" />
                <FmtBtn onMouseDown={(e) => fmt(e, "justifyLeft")}   title="Align left">   <AlignLeftIcon />   </FmtBtn>
                <FmtBtn onMouseDown={(e) => fmt(e, "justifyCenter")} title="Align center"> <AlignCenterIcon /> </FmtBtn>
                <FmtBtn onMouseDown={(e) => fmt(e, "justifyRight")}  title="Align right">  <AlignRightIcon />  </FmtBtn>
                <span className="step-editor-sep" />
                <FmtBtn onMouseDown={insertLink} title="Insert link">         <LinkIcon /> </FmtBtn>
                <FmtBtn onMouseDown={insertHR}   title="Insert horizontal rule"> <HRIcon />  </FmtBtn>
                <span className="step-editor-sep" />
                <select
                    className="step-editor-font-size"
                    title="Font size"
                    onMouseDown={(e) => e.stopPropagation()}
                    onChange={(e) => {
                        editorRef.current?.focus();
                        document.execCommand("fontSize", false, e.target.value);
                        e.target.value = "";
                        emit();
                    }}
                    defaultValue=""
                >
                    <option value="" disabled>Size</option>
                    <option value="1">XS</option>
                    <option value="2">S</option>
                    <option value="3">M</option>
                    <option value="4">L</option>
                    <option value="5">XL</option>
                    <option value="6">2XL</option>
                </select>
                <label className="step-editor-color-btn" title="Text color">
                    <span style={{ borderBottom: "3px solid currentColor" }}>A</span>
                    <input type="color" style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
                        onChange={(e) => { editorRef.current?.focus(); document.execCommand("foreColor", false, e.target.value); emit(); }} />
                </label>
                <label className="step-editor-color-btn" title="Highlight color">
                    <span style={{ background: "rgba(253,224,71,0.6)", padding: "0 3px", borderRadius: 2 }}>H</span>
                    <input type="color" style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
                        onChange={(e) => { editorRef.current?.focus(); document.execCommand("hiliteColor", false, e.target.value); emit(); }} />
                </label>
            </div>

            <div
                ref={editorRef}
                className="step-editor-body"
                contentEditable
                suppressContentEditableWarning
                data-placeholder="Write your email here. Use personalisation tokens below."
                onInput={emit}
                onBlur={emit}
            />

            <TokenBar onInsert={insertToken} />
        </>
    );
};

// ── HTML source editor (code mode) ────────────────────────────────────────────

const HtmlEditor = ({ value, onChange }) => {
    const textareaRef = useRef(null);

    // Token insertion into textarea at cursor
    const insertToken = (token) => {
        const el = textareaRef.current;
        if (!el) return;
        const start = el.selectionStart;
        const end   = el.selectionEnd;
        const next  = el.value.slice(0, start) + token + el.value.slice(end);
        onChange(next);
        // Restore cursor after token
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(start + token.length, start + token.length);
        });
    };

    return (
        <div className="step-html-editor-root">
            {/* Split: code left, preview right */}
            <div className="step-html-split">
                {/* Code panel */}
                <div className="step-html-code-panel">
                    <div className="step-html-panel-label">
                        <CodeIcon /> HTML Source
                    </div>
                    <textarea
                        ref={textareaRef}
                        className="step-html-textarea"
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={"<!DOCTYPE html>\n<html>\n<body>\n  <!-- Your email HTML here -->\n</body>\n</html>"}
                        spellCheck={false}
                        autoCorrect="off"
                        autoCapitalize="off"
                    />
                </div>

                {/* Preview panel */}
                <div className="step-html-preview-panel">
                    <div className="step-html-panel-label">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                        </svg>
                        Live Preview
                    </div>
                    <iframe
                        className="step-html-preview-iframe"
                        srcDoc={value || "<body style='font-family:Arial,sans-serif;color:#64748b;display:flex;align-items:center;justify-content:center;height:100%;margin:0;font-size:13px'>Start typing HTML to see a live preview…</body>"}
                        title="Email preview"
                        sandbox="allow-same-origin"
                        scrolling="yes"
                    />
                </div>
            </div>

            <TokenBar onInsert={insertToken} />
        </div>
    );
};

// ── EmailBodyEditor (mode switcher) ───────────────────────────────────────────

const EmailBodyEditor = ({ value, onChange }) => {
    // Detect if the stored body looks like raw HTML source (has <!DOCTYPE or <html)
    const isRawHtml = (v) => /<!doctype\s+html/i.test(v || "") || /<html[\s>]/i.test(v || "");
    const [mode, setMode] = useState(() => isRawHtml(value) ? "html" : "rich");

    const switchMode = (next) => {
        if (next === mode) return;
        // Warn if switching away from non-empty content
        if (value && value.trim()) {
            const label = next === "html" ? "HTML source" : "rich text";
            const ok = window.confirm(
                `Switch to ${label} mode?\n\nYour current content will be kept as-is but may not transfer perfectly between modes.`
            );
            if (!ok) return;
        }
        setMode(next);
    };

    return (
        <div className="step-editor-root">
            {/* Mode toggle tab strip */}
            <div className="step-editor-mode-bar">
                <button
                    type="button"
                    className={`step-editor-mode-btn${mode === "rich" ? " step-editor-mode-btn--active" : ""}`}
                    onClick={() => switchMode("rich")}
                >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }}>
                        <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                    </svg>
                    Rich Text
                </button>
                <button
                    type="button"
                    className={`step-editor-mode-btn${mode === "html" ? " step-editor-mode-btn--active" : ""}`}
                    onClick={() => switchMode("html")}
                >
                    <CodeIcon />
                    <span style={{ marginLeft: 5 }}>HTML</span>
                </button>
            </div>

            {mode === "rich"
                ? <RichEditor value={value} onChange={onChange} />
                : <HtmlEditor value={value} onChange={onChange} />
            }
        </div>
    );
};

// ── StepCard ──────────────────────────────────────────────────────────────────

const StepCard = ({ step, index, stepNumber, canMoveUp, canMoveDown, showConnector, onChange, onRemove, onMoveUp, onMoveDown }) => {
    const set = (field, value) => onChange(index, { ...step, [field]: value });

    return (
        <div className="crm-step-timeline-item">
            <div className="crm-step-indicator">
                <div className={`crm-step-indicator__circle crm-step-indicator__circle--${step.type}`}>
                    {stepNumber ?? index + 1}
                </div>
                {showConnector && <div className="crm-step-indicator__line" />}
            </div>

            <div className={`crm-step-card crm-step-card--${step.type}`}>
                <div className="crm-step-card__header">
                    <span className="crm-step-card__title">
                        {step.type === "email" ? "Email" : "Delay"}
                    </span>
                    <div className="crm-step-card__controls">
                        <button onClick={() => onMoveUp(index)}   disabled={!canMoveUp}   title="Move up">↑</button>
                        <button onClick={() => onMoveDown(index)} disabled={!canMoveDown} title="Move down">↓</button>
                        <button onClick={() => onRemove(index)}   className="danger"      title="Remove">✕</button>
                    </div>
                </div>

                {step.type === "email" && (
                    <EmailBodyEditor
                        value={step.body || ""}
                        onChange={(html) => set("body", html)}
                    />
                )}

                {step.type === "delay" && (
                    <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", color: "var(--color-text-secondary)", fontSize: "var(--text-sm)" }}>
                        <span>Wait</span>
                        <input
                            type="number"
                            min={1}
                            max={365}
                            value={step.delayDays || 1}
                            onChange={(e) => set("delayDays", Math.max(1, parseInt(e.target.value) || 1))}
                            style={{ width: 60, padding: "2px var(--space-2)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", fontSize: "var(--text-sm)", background: "var(--color-bg-primary)", color: "var(--color-text-primary)", textAlign: "center", outline: "none" }}
                        />
                        <span>day{step.delayDays !== 1 ? "s" : ""} before the next step</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StepCard;
